import { useEffect } from "react";

const SCRAMBLE_CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789#%&*+=-<>/";
const GLYPH_INTERVAL = 50; // ms between random-glyph changes (calmer, more premium)

// Cycle each character through random glyphs, then settle left-to-right.
export function scramble(el) {
  const finalText = el.dataset.scrambleText ?? (el.dataset.scrambleText = el.textContent);
  const chars = [...finalText];
  const len = chars.length;
  const revealSpan = 560; // spread of settle times across the string
  const charDuration = 340; // scramble time before a char settles
  const settle = chars.map((_, i) => (len <= 1 ? 0 : (i / (len - 1)) * revealSpan) + charDuration);
  const totalDuration = Math.max(charDuration, ...settle);
  const start = performance.now();

  if (el._scrambleRAF) cancelAnimationFrame(el._scrambleRAF);

  // Lock the layout so variable-width scrambled glyphs can't reflow the page
  // (headings stay on one line; block text keeps its height).
  const inline = getComputedStyle(el).display.startsWith("inline");
  let unlock;
  if (inline) {
    const prev = el.style.whiteSpace;
    el.style.whiteSpace = "nowrap";
    unlock = () => {
      el.style.whiteSpace = prev;
    };
  } else {
    const height = el.offsetHeight;
    const prevHeight = el.style.height;
    const prevOverflow = el.style.overflow;
    el.style.height = `${height}px`;
    el.style.overflow = "hidden";
    unlock = () => {
      el.style.height = prevHeight;
      el.style.overflow = prevOverflow;
    };
  }

  let lastGlyph = -Infinity;
  const tick = (now) => {
    const t = now - start;
    // Only re-roll the random glyphs every GLYPH_INTERVAL for a calmer morph.
    if (now - lastGlyph >= GLYPH_INTERVAL || t >= totalDuration) {
      lastGlyph = now;
      let out = "";
      for (let i = 0; i < len; i++) {
        const c = chars[i];
        if (c === " ") out += c;
        else if (t >= settle[i]) out += c;
        else out += SCRAMBLE_CHARS[(Math.random() * SCRAMBLE_CHARS.length) | 0];
      }
      el.textContent = out;
    }
    if (t < totalDuration) {
      el._scrambleRAF = requestAnimationFrame(tick);
    } else {
      el.textContent = finalText;
      el._scrambleRAF = null;
      unlock();
    }
  };

  el._scrambleRAF = requestAnimationFrame(tick);
}

// Scrambles every [data-scramble] element inside rootRef: once when the block
// scrolls into view, and again on hover of each line.
export function useScrambleReveal(rootRef, enabled = true) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const targets = [...root.querySelectorAll("[data-scramble]")];
    targets.forEach((el) => {
      el.dataset.scrambleText = el.dataset.scrambleText ?? el.textContent;
    });

    if (!enabled) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          targets.forEach((el, i) => setTimeout(() => scramble(el), i * 90));
          observer.disconnect();
        }
      },
      { threshold: 0.35 }
    );
    observer.observe(root);

    const cleanups = targets.map((el) => {
      const replay = () => scramble(el);
      el.addEventListener("pointerenter", replay);
      return () => el.removeEventListener("pointerenter", replay);
    });

    return () => {
      observer.disconnect();
      cleanups.forEach((off) => off());
    };
  }, [rootRef, enabled]);
}
